import { type VendorDeviceMeta, normalizeVendorDeviceMeta } from '@/delivery-type-plugin/components/vendorDevice';

import { DEFAULT_QUANTITY_UNITS, normalizeQuantityUnit } from '../../../../components/quantityUnit';
import type { ResourceType } from '../../../../platform/pipeline-kernel/types';
import { shouldIncludeMasterGroup } from './nodeGroups';
import type { DisplayRow, MaasNodeGroupsSnapshot, SystemDiskFormValues, ToResourceConfigFn } from './types';

function getSystemDiskGi(values: SystemDiskFormValues, field: 'master.systemDiskSize' | 'worker.systemDiskSize'): number | undefined {
  const raw = values[field];
  return typeof raw === 'number' && raw > 0 ? raw : undefined;
}

function appendOneSystemVolumeConfig(
  result: ResourceType[],
  template: ResourceType,
  sizeGi: number | undefined,
  pricingRaw: VendorDeviceMeta | Record<string, unknown> | undefined,
  toResourceConfig: ToResourceConfigFn,
) {
  const pricing = normalizeVendorDeviceMeta(pricingRaw as VendorDeviceMeta | undefined);
  const config = toResourceConfig(template, {
    vendor: pricing?.vendor,
    device: pricing?.device,
    deviceType: pricing?.deviceType,
    vendorID: pricing?.vendorID,
    deviceID: pricing?.deviceID,
    requirement: sizeGi,
    quantityUnit: normalizeQuantityUnit(pricing?.quantityUnit),
  });
  if (config) result.push(config);
}

export function appendSystemVolumeResourceConfigs(
  result: ResourceType[],
  template: ResourceType,
  values: SystemDiskFormValues,
  nodes: MaasNodeGroupsSnapshot,
  toResourceConfig: ToResourceConfigFn,
) {
  if (shouldIncludeMasterGroup(nodes.runMode)) {
    appendOneSystemVolumeConfig(
      result,
      template,
      getSystemDiskGi(values, 'master.systemDiskSize'),
      values.masterSystemDiskPricing,
      toResourceConfig,
    );
  }

  appendOneSystemVolumeConfig(
    result,
    template,
    getSystemDiskGi(values, 'worker.systemDiskSize'),
    values.workerSystemDiskPricing,
    toResourceConfig,
  );
}

/** 按节点组分别展示系统盘（并入节点配置预览，不再单独成组） */
export function appendPerNodeSystemVolumeDisplayRows(
  values: SystemDiskFormValues,
  nodes: MaasNodeGroupsSnapshot,
  pushRow: (item: DisplayRow) => void,
  translate: (key: string) => string,
  options?: { masterRoleLabel?: string },
) {
  const hasMaster = shouldIncludeMasterGroup(nodes.runMode);
  const masterGi = hasMaster ? getSystemDiskGi(values, 'master.systemDiskSize') : undefined;
  const workerGi = getSystemDiskGi(values, 'worker.systemDiskSize');

  if (hasMaster && nodes.masterNodes[0]) {
    const nodeName = nodes.masterNodes[0].name || 'master';
    pushRow({
      id: 'system-volume-master',
      label: `${translate('common:system.disk.size')} (${options?.masterRoleLabel ?? nodeName})`,
      value: masterGi !== undefined ? `${masterGi} Gi` : '-',
    });
  }

  nodes.workerNodes.forEach((worker, index) => {
    pushRow({
      id: `system-volume-worker-${index}`,
      label: `${translate('common:system.disk.size')} (${worker.name || `worker-${index + 1}`})`,
      value: workerGi !== undefined ? `${workerGi} Gi` : '-',
    });
  });
}

export function appendAggregatedSystemVolumeDisplayRow(
  _template: ResourceType,
  values: SystemDiskFormValues,
  nodes: MaasNodeGroupsSnapshot,
  pushRow: (item: DisplayRow) => void,
  translate: (key: string) => string,
) {
  const hasMaster = shouldIncludeMasterGroup(nodes.runMode);
  const masterGi = hasMaster ? getSystemDiskGi(values, 'master.systemDiskSize') : undefined;
  const workerGi = getSystemDiskGi(values, 'worker.systemDiskSize');
  const masterPricing = normalizeVendorDeviceMeta(values.masterSystemDiskPricing as VendorDeviceMeta | undefined);
  const workerPricing = normalizeVendorDeviceMeta(values.workerSystemDiskPricing as VendorDeviceMeta | undefined);
  // 系统盘容量展示固定 Gi
  const unit = DEFAULT_QUANTITY_UNITS.disk;
  const segments: string[] = [];
  const vendors: string[] = [];

  if (masterGi !== undefined) {
    segments.push(`${masterGi}${unit} (master)`);
    if (masterPricing?.vendor) vendors.push(String(masterPricing.vendor));
  }
  if (workerGi !== undefined) {
    segments.push(`${workerGi}${unit} (worker)`);
    if (workerPricing?.vendor) vendors.push(String(workerPricing.vendor));
  }
  if (!segments.length) return;

  const displayValue = segments.length > 1 ? segments.join('、') : `${masterGi ?? workerGi} ${unit}`;
  const uniqueVendors = Array.from(new Set(vendors.filter(Boolean)));

  pushRow({
    id: 'system-volume',
    label: translate('common:system.disk.size'),
    value: displayValue,
    ...(uniqueVendors.length ? { tag: uniqueVendors.join('、') } : {}),
  });
}
